import { AssetId, CameraId, SiteId, TenantId, InstalledCamera } from "@visioni/a3s-schema";

declare const TopicBrand: unique symbol;
export type Topic = string & { readonly [TopicBrand]: "Topic" };

/**
 * > The maximum number of forward slashes (/) in the MQTT topic name for AWS IoT Core is seven.
 * https://docs.aws.amazon.com/whitepapers/latest/designing-mqtt-topics-aws-iot-core/mqtt-design-best-practices.html
 */
const MAX_TOPIC_PARTS = 8;

/**
 * A wildcard indicating 'any' for a part of a topic in an AWS IAM permission.
 * https://docs.aws.amazon.com/IAM/latest/UserGuide/reference_policies_elements_resource.html
 */
type AWS_IAM_SEGMENT_WILDCARD = "*";

function assertMaxParts(remaining: string[], baseParts: number): void {
  const maxRemaining = MAX_TOPIC_PARTS - baseParts;
  if (remaining.length > maxRemaining)
    throw Error(
      `Topics may only have ${String(MAX_TOPIC_PARTS)} parts total. With ${String(baseParts)} base parts, you can only supply ${String(maxRemaining)} more parts but you supplied ${String(remaining.length)}`,
    );
}

export type InstalledAssetTopicFn = {
  (tenant: string, site: string, asset: string): Topic;
  (tenant: TenantId, site: SiteId, asset: AssetId): Topic;
};

function installedAsset(...remaining: string[]): InstalledAssetTopicFn {
  return (tenant: string | TenantId, site: string | SiteId, asset: string | AssetId): Topic => {
    assertMaxParts(remaining, 4);
    const tenantStr = typeof tenant === "string" ? tenant : TenantId.toString(tenant);
    const siteStr = typeof site === "string" ? site : SiteId.toString(site);
    const assetStr = typeof asset === "string" ? asset : AssetId.toString(asset);
    return `G2/${tenantStr}/${siteStr}/${[assetStr, ...remaining].join("/")}` as Topic;
  };
}

function asset2(...remaining: string[]) {
  return (asset: AWS_IAM_SEGMENT_WILDCARD | AssetId<"fleet">): Topic => {
    assertMaxParts(remaining, 2);
    return `G2/${[asset, ...remaining].join("/")}` as Topic;
  };
}

export type CameraTopicFn = {
  (installed: InstalledCamera): Topic;
  (tenant: string, site: string, asset: string, camera: string): Topic;
  (tenant: TenantId, site: SiteId, asset: AssetId, camera: CameraId): Topic;
};

function camera(...remaining: string[]): CameraTopicFn {
  function makeTopic(tenant: string, site: string, asset: string, camera: string): Topic {
    return `G2/${tenant}/${site}/${[asset, camera, ...remaining].join("/")}` as Topic;
  }

  // Implementation handles all overloads
  return ((
    tenantOrInstalled: string | TenantId | InstalledCamera,
    site?: string | SiteId,
    asset?: string | AssetId,
    cameraId?: string | CameraId,
  ): Topic => {
    assertMaxParts(remaining, 5);

    if (typeof tenantOrInstalled === "object" && "tenant" in tenantOrInstalled) {
      const ic = tenantOrInstalled;
      return makeTopic(
        TenantId.toString(ic.tenant),
        SiteId.toString(ic.site),
        AssetId.toString(ic.asset),
        CameraId.toString(ic.camera),
      );
    }

    // Positional args overload - site/asset/cameraId are required here
    return makeTopic(
      typeof tenantOrInstalled === "string" ? tenantOrInstalled : TenantId.toString(tenantOrInstalled),
      typeof site === "string" ? site : SiteId.toString(site as unknown as SiteId),
      typeof asset === "string" ? asset : AssetId.toString(asset as unknown as AssetId),
      typeof cameraId === "string" ? cameraId : CameraId.toString(cameraId as unknown as CameraId),
    );
  }) as CameraTopicFn;
}

export const Topic = {
  /**
   * @deprecated Use `installedAsset` instead.
   */
  asset: installedAsset,

  /**
   * A message topic for an installed asset.
   * Format: `G2/{tenant}/{site}/{asset}[/...remaining]`
   * @example
   * Topic.installedAsset()("tenant-1", "site-1", "asset-1")
   * // => "G2/tenant-1/site-1/asset-1"
   * @example
   * Topic.installedAsset("info")("tenant-1", "site-1", "asset-1")
   * // => "G2/tenant-1/site-1/asset-1/info"
   */
  installedAsset,

  /**
   * A message topic for a bare asset (without tenant/site context).
   * Format: `G2/{asset}[/...remaining]`
   * @example
   * Topic.asset2()("asset-1")
   * // => "G2/asset-1"
   * @example
   * Topic.asset2("info", "status")("asset-1")
   * // => "G2/asset-1/info/status"
   */
  asset2,

  /**
   * A message topic about a specific camera.
   */
  camera,
};
